import axios from "axios";
import { toast } from "sonner";
import { handleMockRequest } from "./mockApiAdapter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  adapter: handleMockRequest,
});

// Global handler for **any** 401 on an authenticated request.
//
// After login, if a session cookie ever becomes invalid (token expired,
// session revoked from another device, admin deactivated the account, or any
// other reason), the backend returns 401 with details like "Session revoked",
// "Token expired", "Not authenticated" or "User not found". Instead of letting
// each individual page defensively handle these — which is fragile and misses
// cases — the interceptor intercepts globally:
//
//   1) Show a single friendly toast (one-shot to avoid spam if many parallel
//      requests fail at once).
//   2) Hard-redirect to /login after a short beat so the toast is visible.
//   3) Return a **never-resolving** promise so downstream `.then(...)` chains
//      don't run against a rejected promise. This is the key to killing CRA's
//      "Uncaught runtime errors: Request failed with status code 401" red
//      overlay that appears when pages don't `.catch()` their fetches.
//
// /auth/me is the ONE endpoint that's allowed to 401 quietly — it's the "am I
// logged in?" probe that public pages fire on mount. Anonymous visitors on
// public pages must never trigger the toast + redirect.
let _authFailureNotified = false;
const NEVER = new Promise(() => {});
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const path = err?.config?.url || "";
    // Endpoints where 401 is a valid, expected response the calling page must
    // handle itself — e.g. wrong password on the login form, anonymous /auth/me
    // probes from public pages, or forgot-password OTP flows.
    const AUTH_ATTEMPT_PATHS = ["/auth/me", "/auth/login", "/auth/register", "/auth/forgot-password"];
    const isAuthAttempt = typeof path === "string" && AUTH_ATTEMPT_PATHS.some((p) => path.includes(p));
    const alreadyOnLogin = typeof window !== "undefined" && window.location && window.location.pathname === "/login";
    if (status === 401 && !isAuthAttempt && !alreadyOnLogin) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("[api] 401 shortcircuit — redirecting to /login. url=", path, "detail=", err?.response?.data?.detail);
      }
      if (!_authFailureNotified) {
        _authFailureNotified = true;
        try {
          toast.message("You've been signed out — see you soon.");
        } catch (toastErr) {
          if (process.env.NODE_ENV !== "production") console.debug("Auth-failure toast failed", toastErr);
        }
        setTimeout(() => { window.location.assign("/login"); }, 900);
      }
      return NEVER;
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Request failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatINR(n) {
  if (n == null || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (abs >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const loc = localStorage.getItem("lang") === "hi" ? "hi-IN" : "en-IN";
    return d.toLocaleDateString(loc, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
